import type {ReactElement} from 'react';
import React, { useEffect} from 'react';
import {Panel, PanelHeader} from '@Components/panel';
import {BaseFilterThumbnails} from '@Panels/poster-editor-item-filters-panel/components/base-filter-thumbnails';
import {getCurrentPageBackground} from '@Components/poster-editor/poster-editor-reducer';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.class';
import {PosterEditorItemFilters} from '@Panels/poster-editor-item-filters-panel/poster-editor-item-filters-panel.types';
import type {ItemEffectsObject} from '@PosterWhiteboard/classes/item-effects.class';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import type {DeepPartial} from '@/global';
import {useAppSelector} from '@/hooks';

interface ImageBackgroundFiltersOptionsProps {
  panelId: string;

  onBack(): void;

  onClose(): void;
}

export default function ImageBackgroundFiltersOptions({...props}: ImageBackgroundFiltersOptionsProps): ReactElement | null {
  const currentBackgroundObject = useAppSelector(getCurrentPageBackground);

  useEffect((): void => {
    updateOptionsPanelHeight(props.panelId, `158px`);
  }, []);

  if (!currentBackgroundObject) {
    return null;
  }

  if (currentBackgroundObject.details.type !== BackgroundTypeName.IMAGE) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return (
      <PanelHeader
        showBackButton
        onBackButton={(): void => {
          props.onBack();
        }}
        onClose={(): void => {
          props.onClose();
        }}
        title={window.i18next.t('pmwjs_filters')}
      />
    );
  };

  const toggleFilter = (filter: PosterEditorItemFilters): void => {
    if (currentBackgroundObject.details.type !== BackgroundTypeName.IMAGE) {
      return;
    }

    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      return;
    }

    let updatedObject: DeepPartial<ItemEffectsObject> = {};
    const bgImageEffects = currentBackgroundObject.details.imageBackgroundItemObject.effects;
    switch (filter) {
      case PosterEditorItemFilters.BLACK_AND_WHITE:
        updatedObject = {
          blackAndWhite: !bgImageEffects.blackAndWhite,
        };
        break;
      case PosterEditorItemFilters.INVERT:
        updatedObject = {
          invert: !bgImageEffects.invert,
        };
        break;
      case PosterEditorItemFilters.SEPIA:
        updatedObject = {
          sepia: !bgImageEffects.sepia,
        };
        break;
      default:
        return;
    }

    void currentPage.background.updateFromObject({
      details: {
        imageBackgroundItemObject: {
          effects: updatedObject,
        },
      },
    });
  };

  const getContent = (): ReactElement | undefined => {
    if (currentBackgroundObject.details.type !== BackgroundTypeName.IMAGE) {
      return undefined;
    }
    return (
      <BaseFilterThumbnails
        blackAndWhite={currentBackgroundObject.details.imageBackgroundItemObject.effects.blackAndWhite}
        sepia={currentBackgroundObject.details.imageBackgroundItemObject.effects.sepia}
        invert={currentBackgroundObject.details.imageBackgroundItemObject.effects.invert}
        onToggleFilter={toggleFilter}
      />
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
}
